/**
 * The three bottom-bar buttons a person can change (Settings → This phone).
 *
 * The bar is Home · Diary · Ask Mucka · Contacts · Menu. Ask Mucka and Menu
 * are fixed; the other three can each be any page the Menu lists for this
 * person. A default moved off the bar (Home, Diary or Contacts) appears at the
 * top of the Menu instead, above Mucka Skills, so changing the bar never
 * strands a page (Tom, 23 Oct 2026).
 *
 * Per person (Tom, 23 Oct 2026): saved on their `users` row, so it follows
 * them to a new phone. Per workspace too, since what can go there depends on
 * that business's skills and that seat's permissions.
 *
 * Directive-free on purpose: the bar (root layout), the Menu (AuthedShell)
 * and Settings all read it, and a pure helper shared across that boundary
 * belongs in none of them (CLAUDE.md #18).
 */
import { Calendar, House, User } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  MENU_ROW_GATES,
  MENU_ROW_ICONS,
  SKILLS_BY_CATEGORY,
  skillRowVisible,
  type MenuGateContext,
} from './menu-pages';

export interface BarPage {
  /** Stable id, kept in storage and used as the bar's tab key. */
  key: string;
  /** The tab's label. Nine characters at most: tab labels are never shrunk. */
  label: string;
  /** The page's name in Settings' list, where there is room for all of it. */
  name: string;
  href: string;
  icon: LucideIcon;
  allowed: (ctx: MenuGateContext) => boolean;
}

/** The three pages the bar shows until somebody changes it. */
export const DEFAULT_BAR_PAGES: readonly BarPage[] = [
  { key: 'home', label: 'Home', name: 'Home', href: '/overview', icon: House, allowed: () => true },
  { key: 'diary', label: 'Diary', name: 'Diary', href: '/schedule', icon: Calendar, allowed: () => true },
  {
    key: 'clients',
    label: 'Contacts',
    name: 'Contacts',
    href: '/clients',
    icon: User,
    allowed: (ctx) => ctx.perms['view_clients'] === true,
  },
];

/** Slot index → the key it holds when nothing has been chosen. */
export const DEFAULT_SLOT_KEYS = DEFAULT_BAR_PAGES.map((p) => p.key);

/**
 * Every page that can go on the bar: the three defaults, the Menu's fixed
 * rows, and every skill row. DERIVED from the Menu's own lists, never
 * hand-kept, so a row added to the Menu is offered here with the same gate.
 *
 * Not offered: Settings, Share & Earn and Super Admin (account pages, not
 * places you work in), and CIS taken off you (a yearly report).
 */
const FIXED_ROWS: { key: string; label: string; name: string; href: keyof typeof MENU_ROW_GATES }[] = [
  { key: 'actions', label: 'Actions', name: 'Actions', href: '/actions' },
  { key: 'inbox', label: 'Inbox', name: 'Inbox', href: '/inbox' },
  { key: 'jobs', label: 'Jobs', name: 'Live Jobs', href: '/jobs' },
  { key: 'notepad', label: 'Notepad', name: 'Notepad', href: '/notepad' },
  { key: 'properties', label: 'Property', name: 'Properties', href: '/properties' },
  { key: 'team', label: 'Team', name: 'Team', href: '/engineers' },
];

const NOT_OFFERED = new Set(['cis-taken-off-you']);

export const BAR_PAGES: readonly BarPage[] = [
  ...DEFAULT_BAR_PAGES,
  ...FIXED_ROWS.map((r): BarPage => ({
    ...r,
    icon: MENU_ROW_ICONS[r.href],
    allowed: MENU_ROW_GATES[r.href],
  })),
  ...Object.values(SKILLS_BY_CATEGORY)
    .flat()
    .filter((s) => !NOT_OFFERED.has(s.slug))
    .map((s): BarPage => ({
      key: s.slug,
      label: s.barLabel ?? s.label,
      name: s.label,
      href: s.href,
      icon: s.icon,
      allowed: (ctx) => skillRowVisible(s, ctx),
    })),
];

const BY_KEY = new Map(BAR_PAGES.map((p) => [p.key, p]));

export function barPage(key: string | null | undefined): BarPage | undefined {
  return key ? BY_KEY.get(key) : undefined;
}

/** What this person may put on the bar, in the Menu's order. */
export function barChoices(ctx: MenuGateContext): BarPage[] {
  return BAR_PAGES.filter((p) => p.allowed(ctx));
}

// ── Storage ─────────────────────────────────────────────────────────────────
//
// Per PERSON (Tom, 23 Oct 2026): `users.bar_buttons`, so a new phone or a
// reinstall keeps the bar. It reaches the page through getSidebarData, and
// AuthedShell stashes it on `window` with the permissions (the bar is mounted
// outside it). A save updates the stash at once, so the bar moves under the
// thumb, and PUTs it; the caller puts it back if the save fails.

const CHANGE_EVENT = 'mucka-bar-tabs';
const SAVE_PATH = '/api/profile/bar-buttons';

type BarWindow = { __muckaNavBarButtons?: (string | null)[] | null };

/** The published choice, as a string so useSyncExternalStore can compare it. */
export function readBarSlotsRaw(): string {
  if (typeof window === 'undefined') return '';
  const slots = (window as BarWindow).__muckaNavBarButtons;
  return Array.isArray(slots) ? JSON.stringify(slots) : '';
}

/** Slot index → the chosen key, or null where the default stands. */
export function parseBarSlots(raw: string): (string | null)[] {
  let parsed: unknown = null;
  try {
    parsed = raw ? JSON.parse(raw) : null;
  } catch {
    parsed = null;
  }
  const list = Array.isArray(parsed) ? parsed : [];
  return DEFAULT_SLOT_KEYS.map((_, i) => (typeof list[i] === 'string' && BY_KEY.has(list[i]) ? list[i] : null));
}

/**
 * A choice as it is stored: three slots, each a known page key or null, and
 * a slot holding its own default stored as null, so putting a button back
 * really puts it back. Null for anything that is not a valid choice — the
 * route refuses it rather than storing half of it.
 */
export function cleanBarSlots(input: unknown): (string | null)[] | null {
  if (!Array.isArray(input) || input.length !== DEFAULT_SLOT_KEYS.length) return null;
  if (!input.every((k) => k === null || (typeof k === 'string' && BY_KEY.has(k)))) return null;
  return DEFAULT_SLOT_KEYS.map((def, i) => (input[i] && input[i] !== def ? (input[i] as string) : null));
}

/** Show a choice on this page now, before (or without) the save. */
export function publishBarSlots(slots: (string | null)[] | null): void {
  (window as BarWindow).__muckaNavBarButtons = slots;
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

/**
 * Save a choice to the person's row, showing it straight away. Resolves to
 * the response; on anything but ok the previous choice is shown again, and
 * the caller reports the failure.
 */
export async function saveBarSlots(slots: (string | null)[]): Promise<Response> {
  const before = parseBarSlots(readBarSlotsRaw());
  const clean = cleanBarSlots(slots) ?? [null, null, null];
  publishBarSlots(clean);
  try {
    const res = await fetch(SAVE_PATH, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ slots: clean }),
    });
    if (!res.ok) publishBarSlots(before);
    return res;
  } catch (err) {
    publishBarSlots(before);
    throw err;
  }
}

export function subscribeBarSlots(onChange: () => void): () => void {
  window.addEventListener(CHANGE_EVENT, onChange);
  // AuthedShell republishing (a workspace switch, a fresh sidebar read).
  window.addEventListener('mucka-nav-perms', onChange);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange);
    window.removeEventListener('mucka-nav-perms', onChange);
  };
}

// ── Resolving ───────────────────────────────────────────────────────────────

/**
 * What each of the three slots shows for this person: the page they chose, if
 * they may still open it, else the slot's default page, else null (the
 * default is not theirs either: Contacts on an engineer's seat, where the bar
 * decides for itself what goes there).
 *
 * A choice the person has lost access to (a skill switched off, a permission
 * taken away) falls back quietly rather
 * than drawing a tab that 404s. No page is ever on the bar twice.
 */
export function resolveBarSlots(chosen: (string | null)[], ctx: MenuGateContext): (BarPage | null)[] {
  // A page chosen for two slots (storage edited by hand, or written by an
  // older build) counts once: the later slot goes back to its default.
  const taken = new Set<string>();
  const picked = DEFAULT_SLOT_KEYS.map((_, i) => {
    const page = barPage(chosen[i]);
    if (!page || !page.allowed(ctx) || taken.has(page.key)) return null;
    taken.add(page.key);
    return page;
  });
  return picked.map((page, i) => {
    if (page) return page;
    const def = DEFAULT_BAR_PAGES[i];
    if (!def.allowed(ctx) || taken.has(def.key)) return null;
    taken.add(def.key);
    return def;
  });
}

/**
 * The defaults this person can open but has moved off the bar: the rows the
 * Menu adds at its top so changing the bar never strands a page.
 */
export function displacedDefaults(chosen: (string | null)[], ctx: MenuGateContext): BarPage[] {
  const onBar = new Set(resolveBarSlots(chosen, ctx).map((p) => p?.key));
  return DEFAULT_BAR_PAGES.filter((p) => p.allowed(ctx) && !onBar.has(p.key));
}

/**
 * Put `key` in `slot`. Where it already sits in another slot, the two swap,
 * so picking Diary for the first button when it is the second just turns
 * them round rather than leaving a gap.
 */
export function chooseBarSlot(current: (string | null)[], resolved: (BarPage | null)[], slot: number, key: string): (string | null)[] {
  const next = resolved.map((p, i) => current[i] ?? p?.key ?? null);
  const elsewhere = next.findIndex((k, i) => i !== slot && k === key);
  if (elsewhere > -1) next[elsewhere] = next[slot] ?? DEFAULT_SLOT_KEYS[slot];
  next[slot] = key;
  return next;
}

// ── The context, read where AuthedShell publishes it ────────────────────────

type NavWindow = {
  __muckaNavPerms?: Record<string, boolean>;
  __muckaNavSkills?: string[];
  __muckaNavMailbox?: boolean;
  __muckaNavSubbieWork?: boolean;
};

/**
 * The gate context the bottom bar and This phone read. AuthedShell stashes
 * the values on `window` (the bar is mounted outside it) — see the effect
 * there for why a stash and not only an event.
 */
export function navGateContext(glass: boolean, native: boolean): MenuGateContext {
  const w = (typeof window === 'undefined' ? {} : window) as NavWindow;
  return {
    perms: w.__muckaNavPerms ?? {},
    skills: w.__muckaNavSkills ?? [],
    hasMailbox: w.__muckaNavMailbox === true,
    glass,
    native,
    doesSubbieWork: w.__muckaNavSubbieWork === true,
  };
}
